import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui/card';
import { cn } from '@/utils/cn';

/** Sign-in chrome matching the church console. */
export const SignInShell = ({
  form,
  qr,
  showQr = true,
  footer,
}: {
  form: ReactNode;
  qr?: ReactNode;
  /** Hide the QR rail during 2FA or other full-width steps. */
  showQr?: boolean;
  footer?: ReactNode;
}) => {
  const { t } = useTranslation(['admin', 'common']);

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center gap-6 bg-surface-sunken px-4 py-10">
      <div className="text-center">
        <span className="inline-block text-brand">
          <span className="block text-page-title font-semibold">{t('app.name', { ns: 'common' })}</span>
          <span className="mt-0.5 block text-[0.65rem] font-semibold uppercase tracking-[0.28em] text-brand/75">
            {t('app.descriptor', { ns: 'common' })}
          </span>
        </span>
        <p className="mt-1 text-body-sm text-fg-secondary">{t('brand.tagline')}</p>
      </div>

      <Card
        className={cn(
          'overflow-hidden',
          showQr ? 'w-full max-w-[36rem] sm:w-fit sm:max-w-none' : 'w-full max-w-lg',
        )}
      >
        <div className={cn('flex flex-col', showQr && 'sm:flex-row sm:items-stretch')}>
          <div className={cn('p-8', showQr ? 'sm:w-[23rem]' : 'w-full')}>{form}</div>
          {showQr && qr ? (
            <>
              <div className="hidden w-px shrink-0 bg-border sm:block" aria-hidden />
              <div className="flex w-full flex-col items-center justify-center border-t border-border p-6 text-center sm:w-[14rem] sm:shrink-0 sm:border-t-0">
                {qr}
              </div>
            </>
          ) : null}
        </div>
      </Card>

      {footer}
    </div>
  );
};
